/* eslint-disable camelcase */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'
import { arrayOf, getLabel } from '../../../utils/StoreUtils'
import i18n from 'simple-react-i18n'
import DtoSandreCode from '../../../referencial/dto/DtoSandreCode'
import { getFullDate, getHour } from '../../../utils/DateUtil'
import { getSandreLabel } from '../../../utils/StringUtil'
import { SANDRE } from '../../../referencial/constants/ReferencialConstants'
import DtoProductionUnit from '../../../productionUnit/dto/DtoProductionUnit'
import DtoDistributionUnit from '../../../distributionUnit/dto/DtoDistributionUnit'
import DtoQualitometer from '../../../quality/dto/DtoQualitometer'
import DtoPiezometer from '../../../piezometry/dto/DtoPiezometer'
import DtoHydrometricStation from '../../../hydrometry/dto/DtoHydrometricStation'
import DtoInstallation from '../../../installation/dto/installation/DtoInstallation'
import DtoParametrageDataType from '../../../piezometry/dto/DtoParametrageDataType'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { Card, CardContent, Grid2 } from '@mui/material'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import Select from 'components/forms/Select'
import Input from 'components/forms/Input'
import { onChangeHour } from 'utils/FormUtils'
import useSandreList from 'utils/customHook/useSandreList'
import StationDescriptionTable from './StationDescriptionTable'

const headers = ['startDate', 'endDate', 'dataType', 'tempRef', 'comment']

const TempRefDialogContent = ({
    stateElement,
    onChangeElement = () => { },
    dt = [],
}) => {
    const refTempList = useSandreList(SANDRE.REF_TEMPORELLES)

    return (
        <Card>
            <CardContent>
                <Grid2 container columnSpacing={1}>
                    <Grid2 size={6}>
                        <SimpleDatePicker
                            value={stateElement.startDate}
                            label={i18n.startDate}
                            onChange={v => onChangeElement({ startDate: v })}
                            max={stateElement.endDate}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Input
                            title={i18n.startHour}
                            value={getHour(stateElement.startDate)}
                            onChange={v => onChangeHour(v, v2 => onChangeElement({ startDate: v2 }), { max: stateElement.endDate }, stateElement.startDate)}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <SimpleDatePicker
                            value={stateElement.endDate}
                            label={i18n.endDate}
                            onChange={v => onChangeElement({ endDate: v })}
                            min={stateElement.startDate}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Input
                            title={i18n.endHour}
                            value={getHour(stateElement.endDate)}
                            onChange={v => onChangeHour(v, v2 => onChangeElement({ endDate: v2 }), { min: stateElement.startDate }, stateElement.endDate)}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Select
                            options={refTempList}
                            label={i18n.tempRef}
                            value={stateElement.tempRef}
                            onChange={v => onChangeElement({ tempRef: v })}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <Select
                            options={dt}
                            label={i18n.dataType}
                            value={stateElement.typeId}
                            onChange={v => onChangeElement({ typeId: v })}
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <SimpleTextArea
                            title={i18n.comment}
                            value={stateElement.comment}
                            onChange={v => onChangeElement({ comment: v })}
                        />
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    )
}

TempRefDialogContent.propTypes = {
    stateElement: PropTypes.object,
    onChangeElement: PropTypes.func,
    dt: PropTypes.arrayOf(PropTypes.instanceOf(DtoParametrageDataType)),
}

const StationTempRefPanel = ({
    station = {},
    readMode = false,
    onChange = () => { },
    st = '',
}) => {
    const {
        sandreCodes,
        piezometryDataTypes,
        pluviometryDataTypes,
        hydrometryDataTypes,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
    }), shallowEqual)

    const dt = useMemo(() => {
        if (st === 'piezometry') {
            return piezometryDataTypes
        }
        if (st === 'hydrometry') {
            return hydrometryDataTypes
        }
        return pluviometryDataTypes
    }, [st, piezometryDataTypes, hydrometryDataTypes, pluviometryDataTypes])

    return (
        <StationDescriptionTable
            station={station}
            readMode={readMode}
            onChange={onChange}
            keyList='link_tempRefs'
            formatFunction={tr => ({
                ...tr,
                startDate: getFullDate(tr.startDate),
                endDate: getFullDate(tr.endDate),
                tempRef: getSandreLabel(sandreCodes, SANDRE.REF_TEMPORELLES, tr.tempRef),
                dataType: getLabel(dt, tr.typeId),
            })}
            titles={{
                title: i18n.tempRefs,
                edit: i18n.editTempRefs,
                new: i18n.newTempRefs,
            }}
            headers={headers}
            dialogContent={props => <TempRefDialogContent dt={dt} {...props} />}
        />
    )
}

StationTempRefPanel.propTypes = {
    station: PropTypes.oneOfType([
        PropTypes.instanceOf(DtoProductionUnit),
        PropTypes.instanceOf(DtoDistributionUnit),
        PropTypes.instanceOf(DtoQualitometer),
        PropTypes.instanceOf(DtoPiezometer),
        PropTypes.instanceOf(DtoHydrometricStation),
        PropTypes.instanceOf(DtoInstallation),
    ]),
    sandreCodes: arrayOf(DtoSandreCode),
    readMode: PropTypes.bool,
    onChange: PropTypes.func,
    setPopup: PropTypes.func,
    className: PropTypes.string,
    st: PropTypes.string,
}


export default StationTempRefPanel